/**
 * Mention Autocomplete Component
 *
 * A dropdown autocomplete component that triggers when typing @ in a textarea.
 * Allows users to mention other users in comments by searching and selecting
 * from the user directory.
 *
 * Features:
 * - Triggers on @ character
 * - Debounced search
 * - Keyboard navigation
 * - Mouse selection
 * - Positioned relative to cursor in textarea
 */

import { createSpinner } from '../utils/icons';
import { apiClient } from '../services/api-client';
import type { DirectoryUser } from './user-search-picker';

/**
 * Options for the mention autocomplete component
 */
export interface MentionAutocompleteOptions {
  /** The textarea element to attach to */
  textarea: HTMLTextAreaElement;
  /** Callback when a user is selected */
  onSelect: (user: DirectoryUser, startPos: number, endPos: number) => void;
  /** Minimum characters after @ to trigger search (default: 1) */
  minChars?: number;
  /** Debounce delay in ms (default: 200) */
  debounceMs?: number;
  /** Max results to show (default: 5) */
  maxResults?: number;
}

/**
 * API response for user search
 */
interface UserSearchResponse {
  data: DirectoryUser[];
  message: string;
}

/**
 * State for the autocomplete component
 */
interface AutocompleteState {
  isOpen: boolean;
  isLoading: boolean;
  query: string;
  mentionStartPos: number;
  results: DirectoryUser[];
  highlightedIndex: number;
}

/**
 * Create the mention autocomplete component
 */
export function createMentionAutocomplete(options: MentionAutocompleteOptions): {
  destroy: () => void;
} {
  const {
    textarea,
    onSelect,
    minChars = 1,
    debounceMs = 200,
    maxResults = 5,
  } = options;

  // State
  const state: AutocompleteState = {
    isOpen: false,
    isLoading: false,
    query: '',
    mentionStartPos: -1,
    results: [],
    highlightedIndex: -1,
  };

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let abortController: AbortController | null = null;

  // Create dropdown container
  const dropdown = document.createElement('div');
  dropdown.className = `
    mention-autocomplete absolute z-50 max-h-48 w-64 overflow-auto
    rounded-md border border-border bg-neutral-950/95 backdrop-blur-md shadow-lg
    hidden
  `.replace(/\s+/g, ' ').trim();
  dropdown.setAttribute('role', 'listbox');
  dropdown.setAttribute('aria-label', 'User suggestions');

  // Insert dropdown after textarea
  textarea.parentElement?.insertBefore(dropdown, textarea.nextSibling);

  /**
   * Get user initials from display name
   */
  function getInitials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const firstPart = parts[0];
    if (parts.length === 1 || !firstPart) return firstPart?.charAt(0).toUpperCase() ?? '?';
    const lastPart = parts[parts.length - 1];
    return (firstPart.charAt(0) + (lastPart?.charAt(0) ?? '')).toUpperCase();
  }

  /**
   * Position the dropdown relative to the cursor in the textarea
   */
  function positionDropdown(): void {
    const textareaRect = textarea.getBoundingClientRect();
    const parentRect = textarea.parentElement?.getBoundingClientRect() ?? textareaRect;

    // Approximate cursor position - position below textarea
    // For a more accurate position, we'd need to measure text, but this works well for most cases
    const top = textareaRect.bottom - parentRect.top + 4;
    const left = 0;

    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
  }

  /**
   * Open the dropdown
   */
  function openDropdown(): void {
    if (state.isOpen) return;

    state.isOpen = true;
    dropdown.classList.remove('hidden');
    positionDropdown();
  }

  /**
   * Close the dropdown
   */
  function closeDropdown(): void {
    if (!state.isOpen) return;

    state.isOpen = false;
    dropdown.classList.add('hidden');
    state.query = '';
    state.mentionStartPos = -1;
    state.results = [];
    state.highlightedIndex = -1;
  }

  /**
   * Render the dropdown content
   */
  function renderDropdown(): void {
    dropdown.innerHTML = '';

    if (state.isLoading) {
      const loadingEl = document.createElement('div');
      loadingEl.className = 'flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground';
      loadingEl.appendChild(createSpinner({ size: 'sm' }));
      const text = document.createElement('span');
      text.textContent = 'Searching...';
      loadingEl.appendChild(text);
      dropdown.appendChild(loadingEl);
      return;
    }

    if (state.results.length === 0) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'px-3 py-2 text-sm text-muted-foreground';
      emptyEl.textContent = state.query.length < minChars
        ? `Type at least ${minChars} character${minChars > 1 ? 's' : ''}`
        : 'No users found';
      dropdown.appendChild(emptyEl);
      return;
    }

    state.results.forEach((user, index) => {
      const option = document.createElement('div');
      option.className = `
        flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors
        ${index === state.highlightedIndex ? 'bg-neutral-800' : 'hover:bg-neutral-800/50'}
      `.replace(/\s+/g, ' ').trim();
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(index === state.highlightedIndex));
      option.dataset.index = String(index);

      // Avatar
      const avatar = document.createElement('div');
      avatar.className = 'w-7 h-7 rounded-full flex items-center justify-center bg-neutral-800 text-foreground text-xs shrink-0';
      avatar.textContent = getInitials(user.displayName);

      // User info
      const info = document.createElement('div');
      info.className = 'flex-1 min-w-0';

      const nameEl = document.createElement('div');
      nameEl.className = 'text-sm font-medium text-foreground truncate';
      nameEl.textContent = user.displayName;

      const emailEl = document.createElement('div');
      emailEl.className = 'text-xs text-muted-foreground truncate';
      emailEl.textContent = user.email || user.userPrincipalName;

      info.appendChild(nameEl);
      info.appendChild(emailEl);

      option.appendChild(avatar);
      option.appendChild(info);

      // Click handler
      option.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        selectUser(index);
      });

      // Mouse enter for highlighting
      option.addEventListener('mouseenter', () => {
        state.highlightedIndex = index;
        updateHighlight();
      });

      dropdown.appendChild(option);
    });
  }

  /**
   * Update highlight state
   */
  function updateHighlight(): void {
    const options = dropdown.querySelectorAll('[role="option"]');
    options.forEach((opt, index) => {
      const isHighlighted = index === state.highlightedIndex;
      opt.classList.toggle('bg-neutral-800', isHighlighted);
      opt.classList.toggle('hover:bg-neutral-800/50', !isHighlighted);
      opt.setAttribute('aria-selected', String(isHighlighted));
    });
  }

  /**
   * Select a user at the given index
   */
  function selectUser(index: number): void {
    const user = state.results[index];
    if (!user) return;

    // Calculate the end position (current cursor position)
    const endPos = textarea.selectionStart;

    onSelect(user, state.mentionStartPos, endPos);
    closeDropdown();
    textarea.focus();
  }

  /**
   * Search for users
   */
  async function searchUsers(query: string): Promise<void> {
    // Cancel previous request
    if (abortController) {
      abortController.abort();
    }

    if (query.length < minChars) {
      state.results = [];
      state.isLoading = false;
      renderDropdown();
      return;
    }

    state.isLoading = true;
    renderDropdown();

    abortController = new AbortController();

    try {
      const response = await apiClient.get<UserSearchResponse>('/users/search', {
        params: {
          q: query,
          limit: maxResults,
        },
        signal: abortController.signal,
      });

      state.results = response.data.data;
      state.highlightedIndex = state.results.length > 0 ? 0 : -1;
      state.isLoading = false;
      renderDropdown();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Error searching users for mention:', error);
      state.results = [];
      state.isLoading = false;
      renderDropdown();
    }
  }

  /**
   * Check if we're in a mention context and extract the query
   */
  function checkForMention(): { inMention: boolean; query: string; startPos: number } {
    const cursorPos = textarea.selectionStart;
    const text = textarea.value.substring(0, cursorPos);

    // Find the last @ that's not part of an already completed mention
    // Look for @ that's either at the start or preceded by whitespace
    let mentionStart = -1;
    for (let i = text.length - 1; i >= 0; i--) {
      const char = text[i];
      if (char === '@') {
        // Check if it's a valid mention start (at beginning or after whitespace)
        if (i === 0 || /\s/.test(text[i - 1] || '')) {
          mentionStart = i;
          break;
        }
      }
      // Stop if we hit whitespace (no valid @ found before it)
      if (/\s/.test(char || '')) {
        break;
      }
    }

    if (mentionStart === -1) {
      return { inMention: false, query: '', startPos: -1 };
    }

    // Extract the query after @
    const query = text.substring(mentionStart + 1);

    // Don't trigger if query contains certain characters that indicate end of mention
    if (query.includes(']') || query.includes('(') || query.includes(')')) {
      return { inMention: false, query: '', startPos: -1 };
    }

    return { inMention: true, query, startPos: mentionStart };
  }

  /**
   * Handle input changes
   */
  function handleInput(): void {
    const { inMention, query, startPos } = checkForMention();

    if (!inMention) {
      closeDropdown();
      return;
    }

    state.query = query;
    state.mentionStartPos = startPos;

    // Clear existing debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (!state.isOpen) {
      openDropdown();
    }

    // Debounce search
    debounceTimer = setTimeout(() => {
      searchUsers(query);
    }, debounceMs);
  }

  /**
   * Handle keyboard navigation
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (!state.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (state.highlightedIndex < state.results.length - 1) {
          state.highlightedIndex++;
          updateHighlight();
          scrollToHighlighted();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (state.highlightedIndex > 0) {
          state.highlightedIndex--;
          updateHighlight();
          scrollToHighlighted();
        }
        break;

      case 'Enter':
      case 'Tab':
        if (state.highlightedIndex >= 0 && state.results[state.highlightedIndex]) {
          event.preventDefault();
          selectUser(state.highlightedIndex);
        }
        break;

      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
    }
  }

  /**
   * Scroll to keep highlighted option visible
   */
  function scrollToHighlighted(): void {
    const highlighted = dropdown.querySelector(`[data-index="${state.highlightedIndex}"]`);
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * Handle blur - close dropdown with small delay to allow click
   */
  function handleBlur(): void {
    setTimeout(() => {
      if (!dropdown.contains(document.activeElement)) {
        closeDropdown();
      }
    }, 150);
  }

  // Attach event listeners
  textarea.addEventListener('input', handleInput);
  textarea.addEventListener('keydown', handleKeyDown);
  textarea.addEventListener('blur', handleBlur);

  // Click outside to close
  document.addEventListener('click', (event) => {
    if (!textarea.contains(event.target as Node) && !dropdown.contains(event.target as Node)) {
      closeDropdown();
    }
  });

  // Return cleanup function
  return {
    destroy(): void {
      textarea.removeEventListener('input', handleInput);
      textarea.removeEventListener('keydown', handleKeyDown);
      textarea.removeEventListener('blur', handleBlur);
      dropdown.remove();

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (abortController) {
        abortController.abort();
      }
    },
  };
}

/**
 * Format a mention for insertion into textarea
 * Format: @[Display Name](user-id)
 */
export function formatMention(user: DirectoryUser): string {
  return `@[${user.displayName}](${user.id})`;
}

/**
 * Insert a mention into textarea, replacing the @query text
 */
export function insertMention(
  textarea: HTMLTextAreaElement,
  user: DirectoryUser,
  startPos: number,
  endPos: number
): void {
  const mention = formatMention(user);
  const before = textarea.value.substring(0, startPos);
  const after = textarea.value.substring(endPos);

  textarea.value = before + mention + ' ' + after;

  // Set cursor position after the mention
  const newCursorPos = startPos + mention.length + 1;
  textarea.setSelectionRange(newCursorPos, newCursorPos);

  // Dispatch input event to trigger any listeners
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
}

export default createMentionAutocomplete;
